import { useState, useEffect, useCallback } from 'react';
import { Clock, AlertTriangle, X, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { tenantSettingsService, type DeadlineStatus } from '@/services/tenantSettingsService';
import { UserRole, RoleRoutes } from '@/types/global.types';

// Roles that are exempt from profile deadline
const EXEMPT_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.DIRECTOR_GENERAL,
  UserRole.PARENT,
];

export default function DeadlineCountdownBanner() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [deadlineStatus, setDeadlineStatus] = useState<DeadlineStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchDeadlineStatus = useCallback(async () => {
    if (!user) return;

    // Check if user is exempt from deadline
    if (EXEMPT_ROLES.includes(user.role)) {
      setLoading(false);
      return;
    }

    // Don't fetch if profile is already approved or pending
    if (user.profileSubmissionStatus === 'APPROVED' || user.profileSubmissionStatus === 'PENDING_VALIDATION') {
      setLoading(false);
      return;
    }

    try {
      const status = await tenantSettingsService.getMyDeadlineStatus();
      setDeadlineStatus(status);
    } catch (error) {
      console.error('Failed to fetch deadline status:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDeadlineStatus();

    // Refresh deadline status every 5 minutes
    const interval = setInterval(fetchDeadlineStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDeadlineStatus]);

  // Don't render if loading, dismissed, exempt, or no deadline applies
  if (loading || dismissed || !user || !deadlineStatus) {
    return null;
  }

  // Don't show if user has submitted their profile
  if (deadlineStatus.hasSubmittedProfile) {
    return null;
  }

  // Don't show if deadline is not enforced (null deadline)
  if (!deadlineStatus.deadlineDate) {
    return null;
  }

  const daysRemaining = deadlineStatus.daysRemaining ?? 0;
  const isPastDeadline = deadlineStatus.isPastDeadline;
  const isBlocked = deadlineStatus.isBlocked;

  // Determine urgency level
  const getUrgencyLevel = (): 'critical' | 'warning' | 'info' => {
    if (isPastDeadline || isBlocked) return 'critical';
    if (daysRemaining <= 3) return 'critical';
    if (daysRemaining <= 7) return 'warning';
    return 'info';
  };

  const urgency = getUrgencyLevel();

  // Styling based on urgency
  const bannerStyles = {
    critical: 'bg-red-50 border-red-300 text-red-800',
    warning: 'bg-amber-50 border-amber-300 text-amber-800',
    info: 'bg-blue-50 border-blue-300 text-blue-800',
  };

  const iconStyles = {
    critical: 'text-red-500',
    warning: 'text-amber-500',
    info: 'text-blue-500',
  };

  const buttonStyles = {
    critical: 'bg-red-600 hover:bg-red-700 text-white',
    warning: 'bg-amber-600 hover:bg-amber-700 text-white',
    info: 'bg-blue-600 hover:bg-blue-700 text-white',
  };

  // Format remaining time
  const formatTimeRemaining = (): string => {
    if (isPastDeadline || isBlocked) {
      return 'Délai dépassé';
    }

    if (daysRemaining < 0) {
      return 'Délai dépassé';
    }

    if (daysRemaining === 0) {
      return "Dernier jour !";
    }

    if (daysRemaining === 1) {
      return '1 jour restant';
    }

    if (daysRemaining < 7) {
      return `${daysRemaining} jours restants`;
    }

    const weeks = Math.floor(daysRemaining / 7);
    const remainingDays = daysRemaining % 7;

    if (remainingDays === 0) {
      return weeks === 1 ? '1 semaine restante' : `${weeks} semaines restantes`;
    }

    return weeks === 1
      ? `1 semaine et ${remainingDays} jour${remainingDays > 1 ? 's' : ''} restants`
      : `${weeks} semaines et ${remainingDays} jour${remainingDays > 1 ? 's' : ''} restants`;
  };

  // Get message based on status
  const getMessage = (): string => {
    if (isBlocked || isPastDeadline) {
      // Professors keep their access; the banner is a reminder, not a lock
      if (user.role === UserRole.TEACHER || user.isAlsoProfessor === true) {
        return "Votre profil n'a pas été soumis dans les délais. Vous gardez l'accès à vos cours, mais merci de le compléter et de le soumettre au plus vite.";
      }
      return "Votre accès est restreint. Veuillez compléter et soumettre votre profil pour retrouver l'accès complet.";
    }

    if (daysRemaining <= 3) {
      return "Urgent : Complétez votre profil maintenant pour éviter la restriction de votre compte.";
    }

    if (daysRemaining <= 7) {
      return "Rappel : N'oubliez pas de soumettre votre profil pour validation.";
    }

    return "Veuillez compléter et soumettre votre profil pour validation.";
  };

  const handleGoToProfile = () => {
    // Use the central RoleRoutes map so every role lands on its OWN authorized
    // profile route (previously BRANCH_MANAGER & PEDAGOGICAL_DIRECTOR silently
    // fell through to 'dg' → 403). Mirrors the Sidebar's getProfileUrl().
    const roleRoute = RoleRoutes[user.role];
    if (!roleRoute) return;
    navigate(`${roleRoute.replace(':tenantId', user.tenantId || '')}/profile`);
  };

  return (
    <div className={`border-b px-4 py-3 ${bannerStyles[urgency]}`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          {urgency === 'critical' ? (
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${iconStyles[urgency]}`} />
          ) : (
            <Clock className={`w-5 h-5 flex-shrink-0 ${iconStyles[urgency]}`} />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">
                {formatTimeRemaining()}
              </span>
              <span className="text-sm opacity-90">
                — {getMessage()}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleGoToProfile}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${buttonStyles[urgency]}`}
          >
            Compléter mon profil
            <ChevronRight className="w-4 h-4" />
          </button>

          {!isBlocked && !isPastDeadline && urgency !== 'critical' && (
            <button
              onClick={() => setDismissed(true)}
              className="p-1.5 rounded-lg hover:bg-black/5 transition-colors"
              title="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
